import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { FilePdfOutlined } from '@ant-design/icons';
import AnimateButton from 'components/@extended/AnimateButton';
import { CtaBlock } from 'types/blog';
import { inline } from './InlineText';

// ==============================|| 記事中に挟むCTA ||============================== //
//
// 行き先はトップページ('/')の一本に絞っている。
// 資料請求の専用ページではなくトップへ送るのは、トップのファーストビューに
// 資料請求フォームがあり、そこで同じことができるため。
// 記事から来た人は「何のサービスなのか」をまだ知らないので、
// フォームだけの画面にいきなり着地させるより、サービスの説明ごと見せたほうが通じる。
//
// 置く位置は記事データ側が { type: 'cta' } で決める。
//
// 見た目は2種類。
//   カード型(既定)   … 見出し＋説明＋ボタン。読者の関心が高まった山場の直後に置く
//   帯型(compact)    … 1行＋ボタン。章と章のつなぎ目に置く、目立ちすぎない導線
//
// 長い記事にカード型を繰り返し置くと、広告が何度も割り込むように見えて読む手が止まる。
// 「要所はカード、途中は帯」という使い分けで、導線の数を確保しつつ読書の流れを守っている。
//
// 記事本文と同じ幅に収め、全面ティールの帯にはしていないのも同じ理由による。

const primaryColor = '#2aa1af';

const DEFAULT_HEADING = '書類作成の負担を減らしたい方へ';
const DEFAULT_BODY =
    'OHANA AI は、アセスメントから個別支援計画書の作成までをAIが支援するサービスです。機能・料金・導入までの流れをまとめた資料を無料でお配りしています。';

/** ホームへ送るボタン。カード型と帯型で大きさだけを変えて使い回す */
const RequestButton = ({ size }: { size: 'medium' | 'large' }) => (
    <AnimateButton>
        <Button
            component={RouterLink}
            to="/"
            variant="contained"
            size={size}
            startIcon={<FilePdfOutlined />}
            sx={{
                bgcolor: primaryColor,
                fontWeight: 800,
                px: size === 'large' ? 4 : 3,
                whiteSpace: 'nowrap',
                boxShadow: `0 6px 16px ${alpha(primaryColor, 0.3)}`,
                '&:hover': { bgcolor: '#23929f' }
            }}
        >
            無料で資料をもらう
        </Button>
    </AnimateButton>
);

const ArticleCta = ({ block }: { block: CtaBlock }) => {
    if (block.compact) {
        return (
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                    my: { xs: 4, md: 5 },
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 2.5, md: 2.5 },
                    borderRadius: 2,
                    border: `1px solid ${alpha(primaryColor, 0.3)}`,
                    bgcolor: alpha(primaryColor, 0.06)
                }}
            >
                <Typography
                    component="p"
                    sx={{ color: '#0f3d43', fontWeight: 700, fontSize: { xs: '0.93rem', md: '0.98rem' }, lineHeight: 1.85 }}
                >
                    {inline(block.heading ?? DEFAULT_HEADING)}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                    <RequestButton size="medium" />
                </Box>
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                my: { xs: 5, md: 6 },
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(primaryColor, 0.28)}`,
                background: `linear-gradient(135deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(primaryColor, 0.04)} 100%)`
            }}
        >
            <Typography component="p" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#0f3d43', mb: 1.5 }}>
                {block.heading ?? DEFAULT_HEADING}
            </Typography>
            <Typography component="p" sx={{ color: '#4b5563', fontSize: { xs: '0.92rem', md: '0.98rem' }, lineHeight: 2, mb: 3 }}>
                {inline(block.body ?? DEFAULT_BODY)}
            </Typography>
            <RequestButton size="large" />
        </Box>
    );
};

export default ArticleCta;
